import type { Part, SessionStatus } from "@opencode-ai/sdk/v2/client"
import { toolActivity, type ToolActivity } from "./tool-activity"

// A pending tool part is one the model is still WRITING (its arguments stream
// in on `streamedInput`, see tool-activity.ts); a running one executes. The two
// used to share one phase, "Running an action", which said the opposite of
// what was happening for the minutes a model spends writing a file.
type ToolPart = Extract<Part, { type: "tool" }> & { streamedInput?: string }

function toolRevision(part: ToolPart) {
  const output = part.state.status === "running" ? part.state.metadata?.output : undefined
  const streamed = part.state.status === "pending" ? (part.streamedInput?.length ?? 0) : 0
  return `${part.id}:${part.state.status}:${typeof output === "string" ? output.length : 0}:${streamed}`
}

// How long a turn may be silent before its status line says so. With the live
// activity line on, the user asked to watch the machinery: the row says it from
// 30 s. Off — the default since 30.09 — only once the silence looks like a
// hang. A model that does not stream its reasoning (a reseller's V4 Flash,
// GigaChat's answer in one piece) is quiet for a minute and more on an ordinary
// turn, and «Обдумываю · 61 с без новых событий» under it read as an alarm about
// nothing. The plan pill uses the calm threshold too (its ring still stops at
// its own minute — session-todo-dock-state.ts).
export const QUIET_NOTICE_LIVE_MS = 30_000
export const QUIET_NOTICE_CALM_MS = 180_000

// How long the activity line outlives the call it described, so the gap
// between two tool calls does not blank it (see createHeldValue).
export const ACTIVITY_HOLD_MS = 1_500

/**
 * Whether a finished call's line may be held in this phase. Only in the gap
 * between calls — the model thinking, or writing the next one. Once it answers
 * in text or the connection drops, the line describes the past, and the phase
 * ("Пишу ответ", "Переподключаюсь") is what should show at once.
 */
export function holdsActivity(phase: TurnPhase | undefined) {
  return phase !== "responding" && phase !== "connecting" && phase !== "reconnecting"
}

export type TurnPhase = "waiting" | "thinking" | "responding" | "tool" | "preparing" | "connecting" | "reconnecting"
export type TurnProgress = { phase: TurnPhase; revision: string; activity?: ToolActivity }

// What the session status adds to the parts' own story: "waiting" used to mean
// both "the model is thinking" and "the request has not reached the server"
// (2026-09-29: 58 s of VPN trouble, then an upstream that answered in 1.26 s).
// The sidecar now says which (busy.connecting), and a retry says whether it
// failed on the network — shown as reconnecting in the same row instead of an
// error card that clears a second later, attempt after attempt.
export function withConnectionPhase(base: TurnProgress, status: SessionStatus | undefined): TurnProgress {
  if (!status) return base
  if (status.type === "retry" && status.network) {
    return { phase: "reconnecting", revision: `${base.revision}|retry:${status.attempt}` }
  }
  if (status.type === "busy" && status.connecting && base.phase === "waiting") {
    // Reconnecting only after a NETWORK failure (the sidecar attaches its
    // reason); a second attempt after a server refusal is just connecting.
    const reconnect = (status.connecting.attempt ?? 1) > 1 && !!status.connecting.reason
    return {
      phase: reconnect ? "reconnecting" : "connecting",
      revision: `${base.revision}|connecting:${status.connecting.since}`,
    }
  }
  return base
}

export function turnProgress(parts: Part[]): TurnProgress {
  // A retry keeps the dead attempt's tool parts (processor.ts) and a call it
  // was still writing stays `pending` until the turn ends; the re-issued
  // stream's reasoning and text come after it. A pending call older than the
  // newest text or reasoning is that leftover, not the action in progress.
  const lastProse = parts.findLastIndex((part) => part.type === "text" || part.type === "reasoning")
  const active = parts.filter(
    (part, index): part is ToolPart =>
      part.type === "tool" &&
      (part.state.status === "running" || (part.state.status === "pending" && index > lastProse)),
  )
  if (active.length) {
    // The newest call is the one the model is working on; an earlier running
    // one is still named in the revision so its output keeps counting as life.
    const current = active[active.length - 1]
    return {
      phase: current.state.status === "pending" ? "preparing" : "tool",
      revision: active.map(toolRevision).join("|"),
      activity: toolActivity(current),
    }
  }
  const part = parts.findLast((part) => part.type === "text" || part.type === "reasoning" || part.type === "tool")
  if (!part) return { phase: "waiting", revision: "waiting" }
  if (part.type === "tool") return { phase: "waiting", revision: toolRevision(part) }
  if (part.type === "text" || part.type === "reasoning") {
    return {
      phase: part.time?.end ? "waiting" : part.type === "text" ? "responding" : "thinking",
      revision: `${part.id}:${part.text.length}:${part.time?.end ?? ""}`,
    }
  }
  return { phase: "waiting", revision: "waiting" }
}
